/* External */
import { OrderedMap, Record } from 'immutable';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

/* Storage */
import { selectAllChannels } from '../storage/channels';
import { selectIsFetchingChannels } from '../storage/status';
import { fetchAllChannels } from '../storage/channels/actions';

/* Utils */
import { EPG_FILTERS_TYPES, EPG_FILTERS_ID } from '../utils/constants';
import { APP_THEME, channelMinimumSizeToFetch, isNewEpgAllStationsFilterVisible } from '../utils/env';
import { isBrazil } from '../utils/configUtils';

export const Filter = Record({
  id: '',
  name: '',
  category: '',
  type: '',
  emptyMessages: {},
});

/* Categories whose label comes from the translations instead of the raw API value */
const TRANSLATED_CATEGORIES = {
  patrocinado: 'epgFilters.sponsored',
};

export default function useEpgFilters(isNewEPGSupported = false, isNewEPGEnabledForUser = false) {
  const { formatMessage } = useIntl();
  const defaultEmptyMessage = useMemo(() => ({
    title: formatMessage({ id: 'epgEmptyStations.category.title' }),
    description: formatMessage({ id: 'epgEmptyStations.category.description' }),
  }), [formatMessage]);

  const allStations = useMemo(() => Filter({
    id: EPG_FILTERS_ID.ALL_STATIONS,
    name: formatMessage({ id: 'epgFilters.all' }),
    type: EPG_FILTERS_TYPES.ALL,
    emptyMessages: defaultEmptyMessage,
  }), [formatMessage, defaultEmptyMessage]);

  const mostWatched = useMemo(() => Filter({
    id: EPG_FILTERS_ID.MOST_WATCHED,
    name: formatMessage({ id: 'epgFilters.mostWatched' }),
    type: EPG_FILTERS_TYPES.SECTION,
    emptyMessages: {
      title: formatMessage({ id: 'epgEmptyStations.mostWatched.title' }),
      description: formatMessage({ id: 'epgEmptyStations.mostWatched.description' }),
    },
  }), [formatMessage]);

  const dateFilter = useMemo(() => Filter({
    id: EPG_FILTERS_ID.DATE,
    name: formatMessage({ id: 'epgFilters.all' }),
    type: EPG_FILTERS_TYPES.DATE,
    emptyMessages: defaultEmptyMessage,
  }), [formatMessage, defaultEmptyMessage]);

  const recentlyWatched = useMemo(() => Filter({
    id: EPG_FILTERS_ID.RECENTLY_WATCHED,
    name: formatMessage({ id: 'epgFilters.recentlyWatched' }),
    type: EPG_FILTERS_TYPES.SECTION,
    emptyMessages: {
      title: formatMessage({ id: 'epgEmptyStations.recentlyWatched.title' }),
      description: formatMessage({ id: 'epgEmptyStations.recentlyWatched.description' }),
    },
  }), [formatMessage]);

  const forYou = useMemo(() => Filter({
    id: EPG_FILTERS_ID.FOR_YOU,
    name: formatMessage({ id: 'epgFilters.forYou' }),
    type: EPG_FILTERS_TYPES.FOR_YOU,
    emptyMessages: {
      title: formatMessage({ id: 'epgEmptyStations.forYou.title' }),
      description: formatMessage({ id: 'epgEmptyStations.forYou.description' }),
    },
  }), [formatMessage]);

  const desiredOrder = useMemo(() => {
    if (isBrazil && APP_THEME === 'skymais') {
      return [
        'Esportes',
        'Locais & Notícias',
        'Filmes',
        'Séries & Shows',
        'Kids',
        'Internacionais',
      ];
    }

    return [
      'Deportes',
      'Locales & Noticias',
      'Peliculas',
      'Series & Shows',
      'Kids',
      'Internacionales',
    ];
  }, []);

  const channels = useSelector(selectAllChannels);
  const isFetchingChannels = useSelector(selectIsFetchingChannels);
  const dispatch = useDispatch();

  /* Self-healing channel load. The EPG category filters are derived from the
     channels store, which is normally populated by a single fetchAllChannels
     dispatch from Private.jsx at boot. If that fetch fails or is cancelled
     (slow/flaky TV network), the store stays empty and the filter list
     renders only the static filters (Todos / Mais vistos / Recentes) until a
     full page refresh. Whenever a channel-less store is observed and no
     fetch is in flight, (re)dispatch the fetch — the saga retries internally,
     so this loop self-stops the moment channels arrive. */
  const channelsSize = channels?.size ?? 0;
  useEffect(() => {
    const minimumChannelSize = channelMinimumSizeToFetch || 5;
    if (channelsSize <= minimumChannelSize && !isFetchingChannels) {
      dispatch(fetchAllChannels());
    }
  }, [channelsSize, isFetchingChannels, dispatch]);

  const channelCategories = useMemo(() => channels.valueSeq()
    .flatMap((channel) => channel.categories).toSetSeq().toList()
    .sort((categoryA, categoryB) => {
      if (categoryA === categoryB) return 0;
      return categoryA < categoryB ? -1 : 1;
    })
    .sort((categoryA, categoryB) => {
      const indexA = desiredOrder.indexOf(categoryA);
      const indexB = desiredOrder.indexOf(categoryB);

      const positionA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
      const positionB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;

      return positionA - positionB;
    }), [channels, desiredOrder]);

  /* Built with Immutable's own iteration ON PURPOSE: Object.fromEntries is
     polyfilled on webOS 3/4 (no native support before Chrome 73), and the
     index.html fast-shim only handles array-likes (iterable.length) — an
     Immutable List exposes .size, not .length, so the polyfill silently
     returned {} and the category filters vanished on those TVs. Iterating
     the List directly is also faster than materializing an array of pairs
     (no polyfill dispatch, no array build) on old Chromium. */
  const filterCategories = useMemo(() => {
    const entries = {};
    /* eslint-disable-next-line unicorn/no-array-for-each
        -- channelCategories is an Immutable List, not an array; its .forEach
        is the fast internal iteration (no iterator-protocol overhead), which
        matters on webOS 3/4 where this runs on every EPG render. */
    channelCategories?.forEach((category) => {
      const id = category.toUpperCase().replace(/\s/g, '_');
      const translationId = TRANSLATED_CATEGORIES[category.trim().toLowerCase()];
      entries[id] = Filter({
        id,
        name: translationId ? formatMessage({ id: translationId }) : category,
        category,
        type: EPG_FILTERS_TYPES.CATEGORY,
        emptyMessages: defaultEmptyMessage,
      });
    });
    return entries;
  }, [channelCategories, defaultEmptyMessage, formatMessage]);

  const filters = useMemo(() => OrderedMap({
    ...(isNewEPGSupported && isNewEPGEnabledForUser && ({
      [dateFilter.id]: dateFilter,
    })),
    ...((!isNewEPGSupported || !isNewEPGEnabledForUser) && ({
      [allStations.id]: allStations,
      [mostWatched.id]: mostWatched,
    })),
    ...(isNewEPGSupported
      && isNewEPGEnabledForUser && isNewEpgAllStationsFilterVisible && ({
      [allStations.id]: allStations,
    })),
    [recentlyWatched.id]: recentlyWatched,
    ...filterCategories,
  }), [
    isNewEPGSupported,
    isNewEPGEnabledForUser,
    dateFilter,
    allStations,
    mostWatched,
    recentlyWatched,
    filterCategories,
  ]);

  const filtersMiniEpg = useMemo(() => OrderedMap({
    [forYou.id]: forYou,
    ...filterCategories,
  }), [
    filterCategories,
    forYou,
  ]);

  return { filters, filtersMiniEpg };
}
